import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { PersonnelProfiles } from './PersonnelProfiles';
import { MANAGEMENT_TEAM_NAMES, personnelProfilesValue, prefix } from './utils/form';

import styles from './ManagementTeam.module.scss';

export const ManagementTeam: FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_MANAGEMENT_AND_ORGANIZATION.MANAGEMENT_TEAM,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('management.title')}
          badgeText={t('management.team.title')}
          section={FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION}
        />
      </FormSectionCard>
      {/* 7.1.1 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.team.history')}
        aiFieldName={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.HISTORY)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.HISTORY)}
          name={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.HISTORY)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.1.2 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.team.skills')}
        aiFieldName={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.SKILLS)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.SKILLS)}
          name={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.SKILLS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.1.3 */}
      <FormSectionCard
        disabled={!canAccess}
        title={t('management.team.qualities')}
        aiFieldName={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.PERSONAL_QUALITIES)}
      >
        <TextInputControl
          multiline
          control={control}
          id={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.PERSONAL_QUALITIES)}
          name={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.PERSONAL_QUALITIES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      {/* 7.1.4 */}
      <FormSectionCard disabled={!canAccess} title={t('management.team.keyPersonnel')} collapsible>
        <FieldArrayComponent
          name={concatWithDot(prefix, MANAGEMENT_TEAM_NAMES.PERSONNEL_PROFILES)}
          initialItem={personnelProfilesValue}
          buttonText={t('management.team.keyPersonnelButtonText')}
          disabled={!canAccess}
          fieldArrayStyles={{ addButton: styles.addButton }}
        >
          {(namePrefix) => <PersonnelProfiles name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
    </>
  );
};
